import type { EstadisticasUsuario } from '../../types/dashboard';
import { progresoNivel } from '../../types/dashboard';
import { Award, Target, Zap } from 'lucide-react';
import { HistorialSkeleton } from './HistorialActividad';

const cardBase = 'rounded-card border border-v-outline bg-v-surface p-4 shadow-[0_1px_3px_rgb(0_0_0/0.05)]';

/* ------------------------------------------------------------- esqueleto -- */

/**
 * Skeleton con la MISMA forma que el dashboard final. Si las dimensiones
 * difieren, el cambio de skeleton a datos produce CLS, que es justo lo que el
 * gate de la Fase 7 mide.
 */
export function DashboardSkeleton() {
  return (
    <div className="mx-auto w-full max-w-md space-y-4 md:max-w-3xl" aria-busy="true">
      <div className="h-40 animate-pulse rounded-b-[1.75rem] bg-v-background" />

      {/* Antes era la rejilla de siete dias. Ahora que la seccion no existe, el
          hueco lo ocupa la tarjeta de nivel, que es la que queda. Si el skeleton
          no cambia de forma al mismo tiempo que el contenido, aparece un cartel
          fantasma durante la carga. */}
      <div className={cardBase}>
        <div className="flex items-center justify-between gap-3">
          <div className="h-3 w-16 animate-pulse rounded bg-v-outline" />
          <div className="h-5 w-5 animate-pulse rounded-full bg-v-outline" />
        </div>
        <div className="mt-2 flex items-baseline gap-2">
          <div className="h-6 w-24 animate-pulse rounded bg-v-background" />
        </div>
      </div>

      {/* El panel "Lo que has hecho" (cifras + historial) llega REPLEGADO, asi
          que su hueco es una sola tarjeta con su cabecera: dibujar aqui la
          rejilla de cifras seria un cartel fantasma que desaparece al cargar.
          La forma vive en `HistorialSkeleton`, junto al propio panel. */}
      <HistorialSkeleton />

      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className={`${cardBase} flex items-center gap-3`}>
            <div className="h-10 w-10 shrink-0 animate-pulse rounded-xl bg-v-background" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-32 animate-pulse rounded bg-v-background" />
              <div className="h-3 w-48 animate-pulse rounded bg-v-background" />
            </div>
          </div>
        ))}
      </div>

      <span className="sr-only">Cargando tu actividad</span>
    </div>
  );
}

/* ----------------------------------------------------------------- error -- */

/**
 * Error inline con reintento. Antes era un toast y la pagina se renderizaba con
 * todos los ceros, que el estudiante no puede distinguir de "no tengo nada".
 */
export function DashboardError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="mx-auto w-full max-w-md md:max-w-3xl">
      <div className={cardBase}>
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className="grid h-10 w-10 shrink-0 place-items-center rounded-xl"
            style={{ backgroundColor: '#FCE7F3' }}
          >
            <Zap className="h-5 w-5" style={{ color: '#9D174D' }} />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="font-bold text-v-on-surface">No pudimos cargar tu actividad</h2>
            <p className="mt-1 text-sm text-v-on-surface-variant">
              Puede ser un problema de conexión. Tus datos siguen a salvo.
            </p>
            <button
              type="button"
              onClick={onRetry}
              className="mt-3 min-h-11 rounded-pill bg-v-primary px-5 text-sm font-semibold
                         text-white transition-transform active:scale-[0.98]"
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- sin red -- */

export function BannerSinRed() {
  return (
    <div
      role="status"
      className="flex items-center gap-2.5 rounded-card border px-4 py-3"
      style={{ backgroundColor: '#FEF3C7', borderColor: '#F59E0B' }}
    >
      <span aria-hidden className="text-base leading-none">
        ⚠
      </span>
      <p className="text-sm font-medium" style={{ color: '#92400E' }}>
        Sin conexión. Mostrando tu última actividad.
      </p>
    </div>
  );
}

/* ----------------------------------------------------------------- nivel -- */

/**
 * El nivel se muestra como estado, no como barra de presion. El progreso
 * numerico existe pero se muestra solo al activarlo en Preferencias (4.4).
 */
export function NivelCard({
  stats,
  mostrarBarra,
}: {
  stats: EstadisticasUsuario;
  mostrarBarra: boolean;
}) {
  const { pct, faltan, siguiente } = progresoNivel(stats.puntos);

  return (
    <section className={cardBase} aria-labelledby="titulo-nivel">
      <div className="flex items-center justify-between gap-3">
        <h2 id="titulo-nivel" className="text-base font-bold text-v-on-surface">
          Tu nivel
        </h2>
        <Award className="h-5 w-5" style={{ color: '#00BFA6' }} aria-hidden />
      </div>

      <div className="mt-2 flex items-baseline gap-2">
        <span className="text-xl font-extrabold text-v-on-surface">{stats.nivel}</span>
        {mostrarBarra && siguiente && (
          <span className="text-xs text-v-on-surface-variant">
            {pct}% hacia {siguiente}
          </span>
        )}
      </div>

      {mostrarBarra && (
        <div
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Progreso hacia ${siguiente ?? 'el siguiente nivel'}`}
          className="mt-3 h-2 w-full overflow-hidden rounded-full"
          style={{ backgroundColor: '#B8F5EC' }}
        >
          <div
            className="h-2 rounded-full transition-[width] duration-500 motion-reduce:transition-none"
            style={{ width: `${pct}%`, backgroundColor: '#00BFA6' }}
          />
        </div>
      )}

      {siguiente && (
        <p className="mt-2 text-xs text-v-on-surface-variant">
          <Target className="mr-1 inline h-3 w-3" aria-hidden />
          {faltan} {faltan === 1 ? 'punto' : 'puntos'} para {siguiente}
        </p>
      )}
    </section>
  );
}
